/**
 * @file WebSocket 消息信封与 HTTP API 契约。
 *
 * 传输分工（见 ADR-002 / ADR-004）：
 * - **HTTP（Workers）**：无状态操作 —— 建房、查房、匿名身份、健康检查。
 * - **WebSocket（Durable Object）**：有状态操作 —— 一切对局内消息。
 *
 * 所有消息用短字段名（`t` / `d`）压缩，因为 Durable Objects 的计费按
 * 上行消息条数折算（20:1），而 WS 帧大小直接影响移动网络下的手感。
 */

import type { Action, SeatIndex } from '@majiang/core';
import type { SnapshotPayload, WireEvent } from './events';

/** 协议版本。握手时比对，不一致直接拒绝连接（避免半兼容导致的诡异状态）。 */
export const PROTOCOL_VERSION = '1.0.0' as const;

/* ═══════════════════════ 上行：Client → Server ═══════════════════════ */

/** 握手。连接建立后客户端必须先发这条，服务端才会绑定座位。 */
export interface HelloMessage {
  readonly t: 'hello';
  readonly d: {
    readonly protocolVersion: string;
    readonly coreContractVersion: string;
    /** 加入房间时拿到的座位令牌（服务端签发，不可伪造）。 */
    readonly seatToken: string;
    /**
     * 断线重连时带上客户端已处理到的最后 seq。
     * 服务端据此决定「补发增量」还是「推全量快照」。首次连接传 `null`。
     */
    readonly resumeFromSeq: number | null;
  };
}

/** 提交动作。 */
export interface ActionMessage {
  readonly t: 'act';
  /**
   * `Action` 联合已覆盖全部动作类型（含 `timeout` 与响应窗口内的
   * `peng`/`gang`/`hu`/`pass`）。⚠ 客户端发送 `timeout` 一律被服务端以
   * `host_only_action` 拒绝——超时只能由 DO 的 alarm 注入（见 `00-overview.md` §6.6）。
   */
  readonly d: Action;
}

/** 主动请求全量快照（客户端自检发现本地状态可疑时）。 */
export interface ResyncMessage {
  readonly t: 'resync';
  readonly d: { readonly reason: 'gap' | 'checksum_mismatch' | 'manual' };
}

/**
 * 心跳。
 * ⚠ 心跳频率直接乘进 DO 的请求计费（20 条上行 = 1 次计费请求），
 * 因此默认间隔取 25s 且**仅在无其他上行流量时**发送，见零成本部署方案 §4.2。
 */
export interface PingMessage {
  readonly t: 'ping';
  readonly d: { readonly ts: number };
}

/** 预设快捷语（避免开放文本带来的审核成本）。 */
export interface QuickChatMessage {
  readonly t: 'chat';
  readonly d: { readonly presetId: number };
}

/** 上行消息联合。 */
export type ClientMessage =
  | HelloMessage
  | ActionMessage
  | ResyncMessage
  | PingMessage
  | QuickChatMessage;
/** 上行消息类型字面量。 */
export type ClientMessageType = ClientMessage['t'];

/* ═══════════════════════ 下行：Server → Client ═══════════════════════ */

/** 握手成功，附带当前座位视图全量快照。对局未开始时 `snapshot` 为 `null`（等开局）。 */
export interface WelcomeMessage {
  readonly t: 'welcome';
  readonly d: {
    readonly seat: SeatIndex;
    readonly serverTime: number;
    readonly snapshot: SnapshotPayload | null;
  };
}

/**
 * 事件批。服务端把一次 `applyAction` 产生的多条事件合并成一帧下发，
 * 减少 WS 帧数（同样是为了计费与移动端功耗）。
 */
export interface EventsMessage {
  readonly t: 'ev';
  readonly d: { readonly events: readonly WireEvent[] };
}

/** 全量快照（resync 应答，或服务端判定增量不可用时主动推送）。 */
export interface SnapshotMessage {
  readonly t: 'snap';
  readonly d: SnapshotPayload;
}

/** 心跳应答。 */
export interface PongMessage {
  readonly t: 'pong';
  readonly d: { readonly ts: number; readonly serverTime: number };
}

/** 协议级错误（区别于业务级 `action_rejected` 事件）。 */
export interface ErrorMessage {
  readonly t: 'err';
  readonly d: {
    readonly code: ProtocolErrorCode;
    readonly message: string;
    /** 客户端是否应该重连。 */
    readonly retryable: boolean;
  };
}

/** 房间元信息变更（有人进出、托管状态变化）。 */
export interface RoomMessage {
  readonly t: 'room';
  readonly d: {
    readonly roomCode: string;
    readonly members: readonly {
      readonly seat: SeatIndex;
      readonly nickname: string;
      readonly connected: boolean;
      readonly isRobot: boolean;
    }[];
    readonly ownerSeat: SeatIndex;
    readonly started: boolean;
  };
}

/** 下行消息联合。 */
export type ServerMessage =
  | WelcomeMessage
  | EventsMessage
  | SnapshotMessage
  | PongMessage
  | ErrorMessage
  | RoomMessage;

/** 下行消息类型字面量。 */
export type ServerMessageType = ServerMessage['t'];

/** 协议级错误码。 */
export type ProtocolErrorCode =
  | 'protocol_version_mismatch'
  | 'rule_hash_mismatch'
  | 'bad_token'
  | 'room_not_found'
  | 'room_exists'
  | 'room_not_full'
  | 'room_full'
  | 'seat_taken'
  | 'invalid_rule'
  | 'already_started'
  | 'rate_limited'
  | 'malformed_message'
  | 'internal_error';

/* ═══════════════════════ HTTP API 契约 ═══════════════════════ */

/** `POST /api/rooms` —— 建房。 */
export interface CreateRoomRequest {
  readonly nickname: string;
  /** 规则差异项；服务端据此生成不可变规则快照并返回 `ruleHash`。 */
  readonly rulePatch: Readonly<Record<string, unknown>>;
  /** 匿名身份端点签发的 playerId；缺省由服务端生成。 */
  readonly playerId?: string;
}

/** `POST /api/rooms` 应答。 */
export interface CreateRoomResponse {
  readonly roomCode: string;
  readonly seat: SeatIndex;
  readonly seatToken: string;
  readonly ruleHash: string;
  /** WebSocket 连接地址（相对路径，客户端自行补 origin）。 */
  readonly wsPath: string;
}

/** `POST /api/rooms/:code/join` —— 加入房间。 */
export interface JoinRoomRequest {
  readonly nickname: string;
  /** 重连时带上旧 token 以取回原座位。 */
  readonly seatToken: string | null;
  /** 匿名身份端点签发的 playerId；缺省由服务端生成。 */
  readonly playerId?: string;
}

/** `POST /api/rooms/:code/join` 应答。 */
export interface JoinRoomResponse {
  readonly roomCode: string;
  readonly seat: SeatIndex;
  readonly seatToken: string;
  readonly ruleHash: string;
  readonly wsPath: string;
  /** 是否为断线重连（而非首次入座）。 */
  readonly resumed: boolean;
}

/** `POST /api/rooms/:code/start` —— 房主开局。 */
export interface StartRoomRequest {
  /** 房主的座位令牌（服务端据此校验发起人身份）。 */
  readonly seatToken: string;
}

/** `POST /api/rooms/:code/start` 应答。 */
export interface StartRoomResponse {
  readonly started: true;
  readonly gameId: string;
  readonly roomCode: string;
}

/** `GET /api/rooms/:code` —— 查房（不含任何对局内信息）。 */
export interface RoomInfoResponse {
  readonly roomCode: string;
  readonly memberCount: number;
  readonly started: boolean;
  readonly ruleHash: string;
  readonly presetName: string;
}

/** `POST /api/identity` —— 匿名身份。 */
export interface IdentityRequest {
  /** 可选：自定义昵称；缺省由服务端生成随机昵称。 */
  readonly nickname?: string;
}

/** `POST /api/identity` 应答。 */
export interface IdentityResponse {
  readonly playerId: string;
  readonly nickname: string;
}

/** 统一错误响应体。 */
export interface ApiErrorResponse {
  readonly error: ProtocolErrorCode;
  readonly message: string;
}

/** HTTP 路由常量，前后端共用，避免路径字符串两边各写一份。 */
export const API_ROUTES = {
  health: '/api/health',
  createRoom: '/api/rooms',
  identity: '/api/identity',
  joinRoom: (code: string): string => `/api/rooms/${code}/join`,
  roomInfo: (code: string): string => `/api/rooms/${code}`,
  startRoom: (code: string): string => `/api/rooms/${code}/start`,
  websocket: (code: string): string => `/api/rooms/${code}/ws`,
} as const;
